/**
 * Hudba a videa – správa MP3 a videí pro veřejný web.
 * Soubory = bucket web-media/media/ (jeden seznam, bez kategorií).
 *
 * Supabase Storage nepovoluje v názvech diakritiku, proto se soubor uloží
 * pod „bezpečným" názvem a původní (zobrazovaný) název je ve web_settings
 * pod klíčem media_names. Pořadí je ve web_settings.media_order.
 */
import { supabase } from '../lib/supabase.js';
import { isAdmin } from '../lib/auth.js';

const BUCKET = 'web-media';
const ROOT = 'media';
const AUDIO_RE = /\.(mp3|wav|ogg|m4a|aac|flac)$/i;
const VIDEO_RE = /\.(mp4|webm|ogv|mov|m4v)$/i;
const MAX_BYTES = 50 * 1024 * 1024;

function esc(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtMB(b) { return (b / 1048576).toFixed(1) + ' MB'; }
function baseName(name) { return name.replace(/\.[^.]+$/, ''); }

async function getNames() {
    const { data } = await supabase.from('web_settings')
        .select('value').eq('key', 'media_names').maybeSingle();
    const v = data && data.value;
    return (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
}
async function saveNames(map) {
    const { error } = await supabase.from('web_settings')
        .upsert({ key: 'media_names', value: map, updated_at: new Date().toISOString() });
    if (error) console.error('saveNames:', error.message);
}

async function getOrder() {
    const { data } = await supabase.from('web_settings')
        .select('value').eq('key', 'media_order').maybeSingle();
    return Array.isArray(data?.value) ? data.value : [];
}
async function saveOrder(names) {
    const { error } = await supabase.from('web_settings')
        .upsert({ key: 'media_order', value: names, updated_at: new Date().toISOString() });
    if (error) console.error('saveOrder:', error.message);
}

async function listMedia(names) {
    const { data } = await supabase.storage.from(BUCKET)
        .list(ROOT, { limit: 500, sortBy: { column: 'name', order: 'asc' } });
    return (data || [])
        .filter((e) => e.id !== null && (AUDIO_RE.test(e.name) || VIDEO_RE.test(e.name)))
        .map((e) => ({
            key: e.name,
            label: names[e.name] || baseName(e.name),
            size: (e.metadata && e.metadata.size) || 0,
            kind: VIDEO_RE.test(e.name) ? 'video' : 'audio'
        }));
}

function orderBy(items, arr) {
    const idx = new Map((arr || []).map((n, i) => [n, i]));
    return items.slice().sort((a, b) =>
        (idx.has(a.key) ? idx.get(a.key) : 1e9) - (idx.has(b.key) ? idx.get(b.key) : 1e9));
}

export async function renderHudbaVidea(container) {
    container.innerHTML = '<div class="state-banner">Načítám…</div>';

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
        container.innerHTML = '<div class="state-banner">Nejdřív se přihlas.</div>';
        return;
    }

    const admin = await isAdmin();
    const names = await getNames();
    let items = await listMedia(names);
    items = orderBy(items, await getOrder());

    container.innerHTML = `
        <div class="filters">
            <h2 style="font-size:18px; margin-right:auto;">Hudba a videa</h2>
            ${admin ? `
            <label class="btn btn-sm btn-primary" style="display:inline-block;">
                + Nahrát soubory
                <input type="file" accept="audio/*,video/*" multiple id="upl" style="display:none;">
            </label>` : ''}
        </div>
        <p style="font-size:13px; color:var(--text-muted); margin-bottom:4px;">
            ${admin
                ? 'MP3 i videa se ihned zobrazí na veřejném webu (Média → Hudba). Pořadí změníš přetažením. Limit je 50 MB na soubor.'
                : 'Hudbu a videa pro veřejný web vkládá administrátor.'}
        </p>
        <p id="upl-status" style="font-size:13px; color:var(--text-muted); margin:6px 0 14px;"></p>
        ${items.length === 0
            ? '<div class="state-banner">Zatím žádné soubory.</div>'
            : `<div id="media-list" style="display:flex; flex-direction:column; gap:8px;">
                ${items.map((it) => mediaRow(it, admin)).join('')}
               </div>`}
    `;

    container.querySelector('#upl')?.addEventListener('change', async (e) => {
        const files = [...e.target.files];
        if (!files.length) return;
        const status = container.querySelector('#upl-status');
        const nm = await getNames();
        let ok = 0, fail = 0;
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            status.textContent = `Nahrávám ${i + 1}/${files.length} – ${file.name}…`;
            if (file.size > MAX_BYTES) {
                fail++;
                console.error(`Soubor ${file.name} má ${fmtMB(file.size)}, limit je ${fmtMB(MAX_BYTES)}.`);
                continue;
            }
            const ext = (file.name.split('.').pop() || '').toLowerCase();
            const key = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
            const { error: ue } = await supabase.storage.from(BUCKET)
                .upload(`${ROOT}/${key}`, file, { contentType: file.type });
            if (ue) { fail++; console.error('Upload error:', file.name, ue.message); }
            else { ok++; nm[key] = baseName(file.name); }
        }
        await saveNames(nm);
        status.textContent = `Nahráno: ${ok}${fail ? `, selhalo: ${fail} (velikost nad 50 MB)` : ''}.`;
        renderHudbaVidea(container);
    });

    // Přehrávání — dostupné všem uživatelům.
    container.querySelectorAll('[data-play]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const key = btn.dataset.play;
            const kind = btn.dataset.kind;
            const slot = container.querySelector(`.hv-player[data-player-for="${key}"]`);
            if (!slot) return;
            if (slot.dataset.open === '1') {
                slot.innerHTML = '';
                slot.style.display = 'none';
                slot.dataset.open = '0';
                btn.textContent = '▶';
                return;
            }
            btn.textContent = '…';
            const { data, error } = await supabase.storage.from(BUCKET)
                .createSignedUrl(`${ROOT}/${key}`, 3600);
            if (error || !data?.signedUrl) {
                btn.textContent = '▶';
                alert('Nepodařilo se načíst soubor: ' + (error?.message || 'neznámá chyba'));
                return;
            }
            const url = esc(data.signedUrl);
            slot.innerHTML = kind === 'video'
                ? `<video controls autoplay playsinline style="width:100%; max-height:340px; border-radius:8px; background:#000;"><source src="${url}"></video>`
                : `<audio controls autoplay style="width:100%;"><source src="${url}"></audio>`;
            slot.style.display = 'block';
            slot.dataset.open = '1';
            btn.textContent = '⏸';
        });
    });

    // Mazání, přejmenování i řazení jen pro administrátora.
    container.querySelectorAll('[data-del]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const it = items.find((x) => x.key === btn.dataset.del);
            if (!it) return;
            if (!confirm(`Smazat „${it.label}"? Zmizí i z veřejného webu.`)) return;
            const { error: de } = await supabase.storage.from(BUCKET)
                .remove([`${ROOT}/${it.key}`]);
            if (de) { alert('Smazání selhalo: ' + de.message); return; }
            const nm = await getNames();
            if (nm[it.key] !== undefined) { delete nm[it.key]; await saveNames(nm); }
            renderHudbaVidea(container);
        });
    });

    // Přejmenování (dvojklik na název)
    if (admin) {
        container.querySelectorAll('.hv-name').forEach((el) => {
            el.addEventListener('dblclick', () => startRenameMedia(container, el));
        });
    }

    // Řazení přetažením
    const list = admin ? container.querySelector('#media-list') : null;
    if (list) {
        let dragKey = null;
        list.querySelectorAll('[data-row]').forEach((row) => {
            row.addEventListener('dragstart', () => {
                dragKey = row.dataset.row;
                row.style.opacity = '0.4';
            });
            row.addEventListener('dragend', () => { row.style.opacity = ''; });
            row.addEventListener('dragover', (e) => e.preventDefault());
            row.addEventListener('drop', async (e) => {
                e.preventDefault();
                const target = row.dataset.row;
                if (!dragKey || dragKey === target) return;
                const keys = items.map((it) => it.key);
                const from = keys.indexOf(dragKey);
                const to = keys.indexOf(target);
                if (from < 0 || to < 0) return;
                keys.splice(from, 1);
                keys.splice(to, 0, dragKey);
                await saveOrder(keys);
                renderHudbaVidea(container);
            });
        });
    }
}

function mediaRow(it, admin) {
    return `
        <div data-row="${esc(it.key)}" ${admin ? 'draggable="true"' : ''}
             style="display:flex; align-items:center; gap:12px; padding:10px 14px; cursor:${admin ? 'grab' : 'default'};
                    background:var(--surface); color:var(--text); border:1px solid var(--border); border-radius:10px;">
            <span style="font-size:18px;">${it.kind === 'video' ? '🎬' : '🎵'}</span>
            <span class="hv-name" data-key="${esc(it.key)}" ${admin ? 'title="Dvojklik = přejmenovat"' : ''}
                  style="flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis;
                         white-space:nowrap;">${esc(it.label)}</span>
            <span style="font-size:12px; color:var(--text-muted);">${fmtMB(it.size)}</span>
            <button class="btn btn-sm" data-play="${esc(it.key)}" data-kind="${it.kind}" title="Přehrát">▶</button>
            ${admin ? `<button class="btn btn-sm rep-btn-danger" data-del="${esc(it.key)}" title="Smazat">🗑</button>` : ''}
        </div>
        <div class="hv-player" data-player-for="${esc(it.key)}" data-open="0" style="display:none;"></div>`;
}

async function startRenameMedia(container, nameEl) {
    const key = nameEl.dataset.key;
    const oldName = nameEl.textContent.trim();
    const newName = (prompt('Název skladby / videa:', oldName) || '').trim();
    if (!newName || newName === oldName) return;
    const nm = await getNames();
    nm[key] = newName;
    await saveNames(nm);
    renderHudbaVidea(container);
}
